import { useState, useEffect, useCallback } from 'react';
import moment from 'moment/moment';
import 'moment/locale/is';
import { Log } from '@/logging/datadog';

interface CookieHubChoice {
  token: string;
  timestamp: number;
  categories: number[];
  allAllowed: boolean;
}

interface CookieHubState {
  choice?: CookieHubChoice;
}

interface CookieHub {
  openSettings: () => void;
}

interface CookieData {
  cookieId: string | null;
  cookieDate: string | null;
  cookieState: string | null;
}

interface CookieCategoryMap {
  [key: number]: string;
}

declare global {
  interface Window {
    cookiehub?: CookieHub;
    cookiehubState?: CookieHubState;
  }
}

export const useCookieConsent = () => {
  const [cookieData, setCookieData] = useState<CookieData>({
    cookieId: null,
    cookieDate: null,
    cookieState: null,
  });

  const parseCookieState = useCallback((stateData: number[]): string => {
    const cookieCats: CookieCategoryMap = {
      1: 'Nauðsynlegar kökur',
      2: 'Bestunarkökur',
      3: 'Tölfræðikökur',
      4: 'Markaðskökur',
    };

    const arr: string[] = [];
    if (stateData?.length) {
      for (let i = 0; i < stateData.length; i++) {
        const categoryName = cookieCats[stateData[i]];
        if (categoryName) {
          arr.push(categoryName);
        }
      }
      return arr.join(', ');
    }
    return 'Nauðsynlegar kökur';
  }, []);

  const updateCookieData = useCallback((): void => {
    if (typeof window !== 'undefined' && window.cookiehubState?.choice) {
      const choice = window.cookiehubState.choice;

      moment.locale('is');

      setCookieData({
        cookieId: choice.token,
        cookieDate: moment(new Date(choice.timestamp)).utc().format('dddd, MMMM D, YYYY [at] HH:mm:ss [UTC]'),
        cookieState: choice.allAllowed ? 'Allar kökur' : parseCookieState(choice.categories),
      });
    }
  }, [parseCookieState]);

  const openCookieSettings = useCallback((): void => {
    if (typeof window !== 'undefined' && window.cookiehub && typeof window.cookiehub.openSettings === 'function') {
      window.cookiehub.openSettings();

      setTimeout(() => {
        updateCookieData();
      }, 1000);
    } else {
      Log.warning('CookieHub not available or openSettings method not found');
    }
  }, [updateCookieData]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    updateCookieData();

    const handleCookieUpdate = (): void => {
      updateCookieData();
    };

    const events: string[] = [
      'cookieUpdate',
      'cookiehub:ready',
      'cookiehub:consent-changed',
      'cookiehub:settings-changed',
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleCookieUpdate);
    });

    const pollForChanges = setInterval(() => {
      if (window.cookiehubState?.choice) {
        updateCookieData();
      }
    }, 2000);

    const checkCookieHub = (): void => {
      if (window.cookiehub && window.cookiehubState) {
        updateCookieData();
      } else {
        setTimeout(checkCookieHub, 100);
      }
    };

    checkCookieHub();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleCookieUpdate);
      });
      clearInterval(pollForChanges);
    };
  }, [updateCookieData]);

  return {
    cookieData,
    openCookieSettings,
    updateCookieData,
    refreshCookieData: updateCookieData,
  } as const;
};
